import {
  type LucideIcon,
  Activity,
  BrainCircuit,
  ChartNoAxesCombined,
  DatabaseZap,
  Fingerprint,
  GitBranch,
  Globe2,
  HardDrive,
  HeartPulse,
  LockKeyhole,
  PlugZap,
  Shield,
  ShieldCheck,
  UserRound,
  Warehouse,
  Workflow
} from 'lucide-react';

export const lifecycleStages = ['Ingest', 'Govern', 'Operate', 'Monitor'];

export const complianceItems = [
  { id: 'gdpr', name: 'GDPR', icon: Fingerprint },
  { id: 'soc2', name: 'SOC 2', icon: Shield },
  { id: 'iso27001', name: 'ISO 27001', icon: Globe2 },
  { id: 'hipaa', name: 'HIPAA', icon: HeartPulse },
  { id: 'ccpa', name: 'CCPA', icon: UserRound }
] as const;

export const technologies = [
  'Up to 90% Cost Reduction',
  'Zero Cloud Lock-in',
  'High Availability',
  'Automated Backups',
  'End-to-End Encryption',
  'From 30-Minute Setup',
  'Bank-Grade Security',
  'SOC 2 Compliant',
  'AI-Ready Platform',
  'Compliance Made Easy',
  'Zero DevOps Required',
  'Enterprise-Grade'
];

export const computeUnitServices = [
  { label: 'Spark worker', units: 2 },
  { label: 'Kafka broker', units: 2 },
  { label: 'Trino worker', units: 2 },
  { label: 'Airflow worker', units: 1 }
];

export const dataslivPricing = {
  basePlatformFee: 30000,
  computeUnitPrice: 90,
  storageCosts: {
    upTo100Tb: 4,
    upTo1Pb: 3,
    upTo10Pb: 2,
    above10Pb: 1
  }
};

export const cloudBenchmarkSource = {
  title: 'Databricks vs Snowflake vs Fabric Test Details',
  author: 'Josue "Josh" Bogran',
  url: 'https://www.linkedin.com/pulse/databricks-vs-snowflake-fabric-test-details-josue-a-bogran-zcpke/',
  queryCount: 10,
  workload: '3.18B-row coffee shop benchmark'
};

export const cloudComparisonOptions = [
  {
    name: 'Microsoft Fabric',
    queryCost: 1.679,
    hourlyRate: 20,
    storageCostPerTb: 23,
    note: 'Based on article benchmark'
  },
  {
    name: 'Databricks',
    queryCost: 0.8789,
    hourlyRate: 28,
    storageCostPerTb: 25,
    note: 'Based on article benchmark'
  },
  {
    name: 'Snowflake',
    queryCost: 1.42,
    hourlyRate: 24,
    storageCostPerTb: 23,
    note: 'Based on article benchmark'
  }
];

export const companyEnvironments = [
  'Financial services',
  'Telecom operators',
  'Manufacturing',
  'Healthcare groups',
  'Public sector',
  'Energy'
];

export type PlatformCapabilityVariant =
  | 'standard'
  | 'split'
  | 'highlight'
  | 'metrics'
  | 'pipeline'
  | 'timeline'
  | 'compact';

export type PlatformCapability = {
  id: string;
  label: string;
  icon: LucideIcon;
  step: string;
  variant: PlatformCapabilityVariant;
  title: string;
  lead: string;
  description: string;
  outcomes: readonly string[];
  route: readonly string[];
};

export const platformCapabilities: PlatformCapability[] = [
  {
    id: 'platform-data-ingestion',
    label: 'Ingestion',
    icon: DatabaseZap,
    step: '01',
    variant: 'standard',
    title: 'Data ingestion',
    lead: 'Bring every approved source into one controlled intake layer.',
    description:
      'Connect databases, APIs, files, streams, and operational systems without moving ownership away from your environment. Each source is registered with credentials, schema context, sync rules, and health checks before data starts flowing.',
    outcomes: [
      'Batch, streaming, and API intake',
      'Source contracts and ownership',
      'Connection monitoring and retries'
    ],
    route: ['Sources', 'Contracts', 'Validation', 'Landing zone']
  },
  {
    id: 'platform-data-processing',
    label: 'Processing',
    icon: Workflow,
    step: '02',
    variant: 'split',
    title: 'Data processing',
    lead: 'Turn raw movement into repeatable, observable transformation jobs.',
    description:
      'Clean, enrich, join, and prepare data through scheduled or event-driven workflows. Teams can track job status, failed runs, retries, lineage, and SLA pressure without digging through separate scripts and logs.',
    outcomes: [
      'Orchestration for pipelines and tasks',
      'Reusable transformation patterns',
      'Run history, lineage, and alerts'
    ],
    route: ['Validate', 'Transform', 'Enrich', 'Publish']
  },
  {
    id: 'platform-data-lake',
    label: 'Data lake',
    icon: HardDrive,
    step: '03',
    variant: 'highlight',
    title: 'Data lake',
    lead: 'Store raw and curated data in a private, scalable lake foundation.',
    description:
      'Keep high-volume structured, semi-structured, and unstructured data close to your infrastructure. DataSliv organizes landing, bronze, silver, and gold zones with access control, retention rules, and cost-aware storage planning.',
    outcomes: [
      'Raw and curated storage zones',
      'Schema evolution and retention policy',
      'Private cloud or on-prem deployment'
    ],
    route: ['Raw zone', 'Curated zone', 'Catalog', 'Governance']
  },
  {
    id: 'platform-data-analytics',
    label: 'Analytics',
    icon: ChartNoAxesCombined,
    step: '04',
    variant: 'metrics',
    title: 'Data Analytics',
    lead: 'Give teams trusted datasets for reporting, exploration, and decisions.',
    description:
      'Expose governed data to BI dashboards, notebooks, operational reports, and self-service analysis. Analysts get documented datasets while platform teams keep control of permissions, lineage, and refresh quality.',
    outcomes: [
      'BI-ready datasets and semantic views',
      'Notebook and SQL exploration',
      'Metric definitions and refresh checks'
    ],
    route: ['Catalog', 'Metrics', 'Dashboards', 'Insights']
  },
  {
    id: 'platform-data-warehouse',
    label: 'Warehouse',
    icon: Warehouse,
    step: '05',
    variant: 'pipeline',
    title: 'Data warehouse',
    lead: 'Serve business-critical reporting from modeled, reliable data marts.',
    description:
      'Model curated data into warehouse tables, dimensional layers, and domain-specific marts for finance, operations, product, and leadership reporting. Compute and storage stay predictable because warehouse workloads are separated from raw lake activity.',
    outcomes: [
      'Dimensional models and marts',
      'Workload-aware compute planning',
      'Trusted reporting tables'
    ],
    route: ['Model', 'Optimize', 'Serve', 'Audit']
  },
  {
    id: 'platform-mlops',
    label: 'MLOps',
    icon: GitBranch,
    step: '06',
    variant: 'timeline',
    title: 'MLOps',
    lead: 'Move models from experiments to governed, repeatable operations.',
    description:
      'Coordinate feature preparation, training jobs, model registry workflows, approvals, deployments, and monitoring. Data scientists can iterate while operations teams keep reproducibility, rollback paths, and compliance evidence visible.',
    outcomes: [
      'Feature and training pipelines',
      'Model registry and release checks',
      'Monitoring for drift and quality'
    ],
    route: ['Features', 'Train', 'Register', 'Monitor']
  },
  {
    id: 'platform-ai-integration',
    label: 'AI integration',
    icon: BrainCircuit,
    step: '07',
    variant: 'compact',
    title: 'LLM & AI integration',
    lead: 'Connect private data to AI workflows without losing control.',
    description:
      'Prepare governed datasets, vector indexes, retrieval workflows, and AI application hooks for internal assistants and automation. Sensitive context stays inside approved boundaries while teams get the benefits of modern LLM and AI interfaces.',
    outcomes: [
      'Private retrieval and vector search',
      'Governed AI application context',
      'Human review and audit trails'
    ],
    route: ['Context', 'Retrieval', 'Guardrails', 'AI apps']
  }
];

export const platformCapabilityMeta = platformCapabilities.map(
  ({ icon, id, step, variant }) => ({
    icon,
    id,
    step,
    variant
  })
);

export const buildVsBuyStats = [
  {
    value: '4',
    label: 'operating layers',
    detail: 'source mapping, policy, operations, and delivery checks'
  },
  {
    value: '30 min',
    label: 'to a contained start',
    detail: 'map the first workflow before a large migration plan'
  },
  {
    value: '0',
    label: 'cloud lock-in required',
    detail: 'keep the runtime on-prem or inside a private cloud'
  }
];

export const buildInHouseCosts = [
  {
    title: 'Source discovery',
    description:
      'A spreadsheet of databases, owners, credentials, and schemas goes stale as soon as teams change.'
  },
  {
    title: 'Policy enforcement',
    description:
      'Masking, retention, approvals, and exceptions have to be rebuilt around every new data route.'
  },
  {
    title: 'Operational handoffs',
    description:
      'Retries, queues, assignments, and SLA pressure become custom platform work instead of data work.'
  },
  {
    title: 'Audit readiness',
    description:
      'Run history, approvals, and output evidence end up scattered across logs, tickets, and chats.'
  },
  {
    title: 'Maintenance',
    description:
      'Every policy change, schema drift, and downstream request becomes another engineering ticket.'
  },
  {
    title: 'Predictable cost',
    description:
      'Infrastructure, code review, support, and dispute handling keep growing outside one operating model.'
  }
];

export const dataslivBuyAdvantages = [
  {
    title: 'Private source map',
    description:
      'Register databases, files, streams, and services with ownership and connection context in one place.'
  },
  {
    title: 'Policy-first workflows',
    description:
      'Release data movement through approval, masking, retention, and exception checks before jobs run.'
  },
  {
    title: 'Observable operations',
    description:
      'Track queues, retries, ownership, SLA pressure, and run status without exposing private systems.'
  },
  {
    title: 'Audit history',
    description:
      'Keep searchable evidence for sources, decisions, jobs, and outputs attached to every workflow.'
  },
  {
    title: 'Controlled delivery',
    description:
      'Send approved data to BI, exports, notebooks, APIs, and apps with delivery history preserved.'
  },
  {
    title: 'Predictable footprint',
    description:
      'Start contained, expand workflow by workflow, and model compute with clear platform pricing.'
  }
];

export const infrastructureCards = [
  {
    icon: PlugZap,
    title: 'Private data paths',
    description:
      'Connect sources, jobs, and reporting layers across internal networks.'
  },
  {
    icon: Workflow,
    title: 'Controlled execution',
    description:
      'Run governed workflows with approvals, ownership, and clear operational status.'
  },
  {
    icon: ShieldCheck,
    title: 'Audit-ready activity',
    description:
      'Track who changed what, when jobs moved, and where exceptions happened.'
  },
  {
    icon: GitBranch,
    title: 'Policy controls',
    description:
      'Keep workflow rules, access paths, and release conditions explicit.'
  },
  {
    icon: Activity,
    title: 'Operational visibility',
    description:
      'Give teams one place to see movement, failures, approvals, and dependencies.'
  }
];

export const infrastructureCardIcons = infrastructureCards.map(
  ({ icon }) => icon
);

export const contactSignal = {
  icon: LockKeyhole,
  text: 'Discovery starts with your current data movement, approval, and reporting bottlenecks.'
};
